import React, { useState } from 'react';
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonArray = JsonValue[];
interface JsonObject {
    [key: string]: JsonValue;
}
interface InlineResponseViewerProps {
    data: JsonObject;
    onPathCopy?: (path: string, e: React.MouseEvent) => void;
}

const InlineResponseViewer: React.FC<InlineResponseViewerProps> = ({ data, onPathCopy }) => {
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

    const togglePath = (path: string): void => {
        const newPaths = new Set(expandedPaths);
        if (newPaths.has(path)) {
            newPaths.delete(path);
        } else {
            newPaths.add(path);
        }
        setExpandedPaths(newPaths);
    };

    const renderValue = (value: unknown, path: string = ''): React.ReactNode => {
        if (value === null) return <span className="text-muted">null</span>;
        if (typeof value === 'boolean') return <span className="text-primary">{value.toString()}</span>;
        if (typeof value === 'number') return <span className="text-info">{value}</span>;
        if (typeof value === 'string') return <span className="text-success">"{value}"</span>;

        if (Array.isArray(value)) {
            const isExpanded = expandedPaths.has(path);
            return (
                <div>
                    <span
                        className="user-select-none text-secondary"
                        onClick={() => togglePath(path)}
                        style={{ cursor: 'pointer' }}
                    >
                        {isExpanded ? '▼' : '▶'} Array[{value.length}]
                    </span>
                    {isExpanded && (
                        <div className="ms-4 border-start border-2 ps-2">
                            {value.map((item, index) => (
                                <div key={index} className="my-1">
                                    <span className="text-muted">{index}: </span>
                                    {renderValue(item, `${path}.${index}`)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        if (typeof value === 'object') {
            const isExpanded = expandedPaths.has(path);
            return (
                <div>
                    <span
                        className="user-select-none text-secondary"
                        onClick={() => togglePath(path)}
                        style={{ cursor: 'pointer' }}
                    >
                        {isExpanded ? '▼' : '▶'} Object
                    </span>
                    {isExpanded && (
                        <div className="ms-4 border-start border-2 ps-2">
                            {Object.entries(value as Record<string, unknown>).map(([key, val]) => (
                                <div key={key} className="my-1">
            <span
                className="text-muted"
                onClick={(e) => {
                    const fullPath = path ? `${path}.${key}` : key;
                    if (onPathCopy) {
                        onPathCopy(fullPath, e);
                    } else {
                        navigator.clipboard.writeText(fullPath);
                    }
                    e.stopPropagation();
                }}
                style={{ cursor: 'pointer' }}
                title="Click to copy path"
            >
                {key}:
            </span>{' '}
                                    {renderValue(val, path ? `${path}.${key}` : key)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return String(value);
    };

    if (!data) return null;

    return (
        <div className="bg-light rounded border p-3 mt-2 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-medium">Response Data</h6>
                <small className="text-muted">(Click on field names to copy path)</small>
            </div>
            <div className="font-monospace small" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {renderValue(data, '')}
            </div>
        </div>
    );
};

export default InlineResponseViewer;